import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignIn = ({ onSignIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signin`, {
        email: email.trim(),
        password: password
      });

      const { access_token, user } = response.data;

      // Store token and user
      localStorage.setItem('chatAppToken', access_token);
      localStorage.setItem('chatAppUser', JSON.stringify(user));

      onSignIn(user);
      toast.success('Welcome back!');

      // Redirect based on role
      if (user.role === 'super_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error signing in:', error);
      const message = error.response?.data?.detail || 'Failed to sign in. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundColor: '#2e0510'
      }}
    >


      <Card
        className="w-full max-w-md shadow-2xl border-2 relative z-10"
        style={{
          backgroundColor: '#260e10',
          borderColor: 'rgba(255, 200, 87, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        }}
      >
        <CardHeader className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="w-7 h-7 animate-pulse" style={{ color: '#FFC857' }} />
            <CardTitle className="text-4xl font-bold" style={{ color: '#FFC857', textShadow: '0 0 20px rgba(255, 200, 87, 0.5)' }}>Sign In</CardTitle>
          </div>
          <CardDescription className="text-base" style={{ color: '#F5E3D7' }}>Welcome back to Chat App</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#FFC857', opacity: 0.7 }} />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 text-base border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#FFC857', opacity: 0.7 }} />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 pr-11 h-12 text-base border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: '#FFC857', opacity: 0.7 }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm hover:underline"
                style={{ color: '#FFC857' }}
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-lg font-bold transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: '#f3b15b',
                color: '#47030F',
                boxShadow: '0 2px 10px rgba(255, 200, 87, 0.3)',
                borderRadius: '8px'
              }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="text-center text-sm" style={{ color: '#F5E3D7' }}>
              Don't have an account?{' '}
              <Link to="/signup" className="font-semibold hover:underline" style={{ color: '#FFC857' }}>
                Sign Up
              </Link>
            </div>

            {/* Google Sign-In */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'rgba(255, 200, 87, 0.2)' }}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2" style={{ backgroundColor: '#260e10', color: '#F5E3D7', opacity: 0.7 }}>or</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  try {
                    const response = await axios.post(`${API}/auth/google`, {
                      credential: credentialResponse.credential
                    });
                    const { access_token, user } = response.data;
                    localStorage.setItem('chatAppToken', access_token);
                    localStorage.setItem('chatAppUser', JSON.stringify(user));
                    onSignIn(user);
                    toast.success('Welcome!');
                    if (user.role === 'super_admin') {
                      navigate('/admin');
                    } else {
                      navigate('/dashboard');
                    }
                  } catch (error) {
                    console.error('Google sign-in error:', error);
                    toast.error('Google sign-in failed');
                  }
                }}
                onError={() => toast.error('Google sign-in failed')}
                theme="filled_black"
                size="large"
                width="300"
              />
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignIn;